import React from "react";
import { useTranslation } from "react-i18next";

import { ContainerValue } from "components/containers";
import { Button, TitleH2, TitleH3, TitleH4 } from "components/generic";
import { DateLine, SpokenLanguages } from "pages/ProfileUser/SimpleComponents";

import {
  AboutStudent,
  AboutStudentList,
  FlexContainer,
  ImageTextContainer
} from "./styles";
import { IUserShort } from "interfaces/interfaces";
import { getNameCountry } from "utils/fetchMapInfo";
import { useContextHospi } from "context/ContextHospi";
import { TLang } from "assets/data/isoNameLanguage";
import { activeYears, getDateOrNone, isoDate } from "utils/helpers";
import { useCalendar } from "components/Calendar";

export const StudentAboutInfo: React.FC<
  Partial<IUserShort> & {
    closeBtn: (x: number) => void;
    isPreviewMode?: boolean;
  }
> = ({
  thumbnail_url,
  first_name,
  last_name,
  gender,
  birth_date,
  birth_country,
  spoken_languages,
  description,
  privacy,
  autonomy,
  requested_autonomy,
  active_years,
  house_rents_count,
  hosted_tenants_count,
  preferred_move_in,
  preferred_move_out,
  closeBtn,
  isPreviewMode = false
}) => {
  const { t, i18n } = useTranslation("matches");
  const { currentUserRole } = useContextHospi();

  const name = `${first_name} ${last_name}`;
  const age =
    new Date().getFullYear() - new Date(birth_date ?? new Date()).getFullYear();

  const countTimes = house_rents_count ?? hosted_tenants_count ?? 0;

  const moveInDate = useCalendar({
    value: getDateOrNone(preferred_move_in)
  });
  const moveOutDate = useCalendar({
    value: getDateOrNone(preferred_move_out)
  });

  if (!first_name && !last_name)
    return (
      <div>
        <TitleH2 style={{ padding: "24px 0 0 24px" }}>Warning</TitleH2>
        <TitleH3 style={{ margin: "24px 24px 0" }}>
          Something went wrong, 103
        </TitleH3>
        <Button
          main
          onClick={() => closeBtn(0)}
          style={{ float: "right", margin: "24px", fontSize: "18px" }}
        >
          {t(`${currentUserRole}.userInfo.close`)}
        </Button>
      </div>
    );

  const currLang = i18n.language.toUpperCase() as TLang;
  return (
    <>
      <TitleH2>
        {t(
          `${
            isPreviewMode && currentUserRole === "student" ? "student" : "host"
          }.userInfo.title`
        )}
      </TitleH2>
      <FlexContainer>
        <AboutStudent>
          <ContainerValue>
            <TitleH2>
              {t(
                `${
                  isPreviewMode && currentUserRole === "student"
                    ? "student"
                    : "host"
                }.userInfo.about`
              )}
            </TitleH2>
            <ImageTextContainer>
              <img
                src={thumbnail_url + `?${new Date().toString()}`}
                alt="Student"
              />
              <div className="textContainer">
                <TitleH3>{name}</TitleH3>
                <TitleH4>
                  <strong>
                    {activeYears(active_years ?? 0) === "1" &&
                      t(
                        `${
                          isPreviewMode && currentUserRole === "student"
                            ? "student"
                            : "host"
                        }.userInfo.LessThan`
                      ) +
                        " " +
                        activeYears(active_years ?? 0)}
                  </strong>{" "}
                  {t(
                    `${
                      isPreviewMode && currentUserRole === "student"
                        ? "student"
                        : "host"
                    }.userInfo.yearsActive`
                  )}
                </TitleH4>
                <TitleH4>
                  {countTimes === 0 &&
                    t(
                      `${
                        isPreviewMode && currentUserRole === "student"
                          ? "student"
                          : "host"
                      }.userInfo.roomsRented.0`
                    )}
                  {countTimes > 0 && (
                    <>
                      {" "}
                      <strong>{countTimes}</strong>{" "}
                      {t(
                        `${currentUserRole}.userInfo.roomsRented.${
                          countTimes === 1 ? 1 : 2
                        }`
                      )}
                    </>
                  )}
                </TitleH4>
              </div>
            </ImageTextContainer>
            <AboutStudentList>
              <li key="gender">
                {t(`${currentUserRole}.userInfo.gender.${gender}`)}
              </li>
              {(currentUserRole === "host" || isPreviewMode) && (
                <>
                  <li key="age">
                    {age} {t("host.card.yearOld")}
                  </li>
                  <li key="country">
                    {getNameCountry(birth_country || "", currLang)}
                  </li>
                </>
              )}
            </AboutStudentList>
            {spoken_languages && spoken_languages.length > 0 && (
              <SpokenLanguages items={spoken_languages || ""} />
            )}
            <TitleH3 m="5px 0px">
              {t(`${currentUserRole}.userInfo.preferredDate`)}
            </TitleH3>
            <div className="dates">
              <DateLine
                start={isoDate(moveInDate.value)}
                end={isoDate(moveOutDate.value)}
                ico
                context="profileOverview"
              />
            </div>
            <TitleH3 m="5px 0px">
              {t(
                `${
                  isPreviewMode && currentUserRole === "student"
                    ? "student"
                    : "host"
                }.userInfo.description`
              )}
            </TitleH3>
            <div className="description">{description}</div>
          </ContainerValue>
        </AboutStudent>
        {/* <SocialPreference>
          <ContainerValue>
            <TitleH2 m="0 0 20px 0">
              {t(
                `${
                  isPreviewMode && currentUserRole === "student"
                    ? "student"
                    : "host"
                }.userInfo.preference`
              )}
            </TitleH2>
            <SocialPreferences
              roleUser={
                isPreviewMode
                  ? "student"
                  : currentUserRole === "student"
                  ? "host"
                  : "student"
              }
              privacy={privacy_var}
              newInTown={autonomy_var}
            />
          </ContainerValue>
          <Button
            main
            onClick={() => closeBtn(0)}
            style={{ float: "right", padding: "8px 40px", fontSize: "18px" }}
          >
            {t(`${currentUserRole}.userInfo.close`)}
          </Button>
        </SocialPreference> */}
      </FlexContainer>
    </>
  );
};
